import React, { useEffect, useMemo, useState } from 'react';
import api from '../config/axios';

const PROVIDERS = [
  { value: 'shopify', label: 'Shopify' },
  { value: 'ikas', label: 'ikas' },
  { value: 'ticimax', label: 'Ticimax' }
];

function safeJsonParse(input, fallback) {
  try {
    return JSON.parse(input);
  } catch (e) {
    return fallback;
  }
}

function buildWebhookUrl(integrationId, provider) {
  const base = window.location.origin;
  return `${base}/api/integrations/${integrationId}/webhook/${provider}`;
}

function getFirmHeaders(firmSlug) {
  return {
    'x-firm-slug': firmSlug
  };
}

export default function WebhookIntegrationsPanel({ firmSlug }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [ui, setUi] = useState({
    createAdvanced: false,
    editAdvanced: false,
    toolsOpen: false,
    filtersOpen: false
  });

  const [integrations, setIntegrations] = useState([]);
  const [selectedIntegration, setSelectedIntegration] = useState(null);

  const [createForm, setCreateForm] = useState({
    provider: 'shopify',
    status: 'active',
    webhook_secret: '',
    enqueue_to_message_pool: false,
    default_country: 'TR'
  });

  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const [events, setEvents] = useState([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsLimit, setEventsLimit] = useState(50);
  const [eventsOffset, setEventsOffset] = useState(0);
  const [eventsFilter, setEventsFilter] = useState({
    status: '',
    event_type: '',
    provider_event_id: '',
    raw_body_sha256: '',
    payload_sha256: ''
  });

  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [eventModalLoading, setEventModalLoading] = useState(false);
  const [eventModalErr, setEventModalErr] = useState('');
  const [eventModalData, setEventModalData] = useState(null);
  const [eventModalActions, setEventModalActions] = useState([]);

  const [processLoading, setProcessLoading] = useState(false);
  const [processResult, setProcessResult] = useState(null);
  const [processErr, setProcessErr] = useState('');

  const [testSending, setTestSending] = useState(false);
  const [testErr, setTestErr] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testForm, setTestForm] = useState({
    event_type: '',
    provider_event_id: '',
    payload_json: '{\n  "test": true\n}',
    headers_json: '{\n}',
    use_provided_signature: false,
    force_bad_signature: false
  });

  const webhookUrl = useMemo(() => {
    if (!selectedIntegration) return '';
    return buildWebhookUrl(selectedIntegration.id, selectedIntegration.provider);
  }, [selectedIntegration]);

  const eventsStats = useMemo(() => {
    const counts = { received: 0, processing: 0, retry: 0, processed: 0, ignored: 0, failed: 0 };
    for (const ev of events || []) {
      const k = String(ev?.status || '').toLowerCase();
      if (counts[k] !== undefined) counts[k] += 1;
    }
    const latest = (events || []).length ? events[0] : null;
    return { counts, latest };
  }, [events]);

  async function copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(String(text || ''));
      setSuccess('Kopyalandı');
    } catch (e) {
      setError('Kopyalama başarısız');
    }
  }

  async function openEventModal(ev) {
    if (!selectedIntegration || !ev?.id) return;
    setEventModalOpen(true);
    setEventModalLoading(true);
    setEventModalErr('');
    setEventModalData(null);
    setEventModalActions([]);

    try {
      const [detailRes, actionsRes] = await Promise.all([
        api.get(`/firms/${firmSlug}/integrations/${selectedIntegration.id}/events/${ev.id}`, {
          headers: getFirmHeaders(firmSlug)
        }),
        api.get(`/firms/${firmSlug}/integrations/${selectedIntegration.id}/events/${ev.id}/actions`, {
          headers: getFirmHeaders(firmSlug)
        })
      ]);
      setEventModalData(detailRes?.data?.data || null);
      setEventModalActions(actionsRes?.data?.data || []);
    } catch (e) {
      setEventModalErr(e?.error || e?.message || 'Event detayı alınamadı');
    } finally {
      setEventModalLoading(false);
    }
  }

  async function loadIntegrations({ selectId } = {}) {
    setError('');
    setSuccess('');
    const res = await api.get(`/firms/${firmSlug}/integrations`, {
      headers: getFirmHeaders(firmSlug)
    });

    const rows = res?.data?.data || [];
    setIntegrations(rows);

    if (selectId) {
      const found = rows.find(r => String(r.id) === String(selectId));
      setSelectedIntegration(found || null);
      if (found) {
        setEditForm({
          id: found.id,
          provider: found.provider,
          status: found.status,
          enqueue_to_message_pool: !!found?.settings?.enqueue_to_message_pool,
          default_country: found?.settings?.default_country || 'TR',
          webhook_secret: ''
        });
      } else {
        setEditForm(null);
      }
      return;
    }

    if (rows.length > 0) {
      // Keep current selection if exists
      const currentId = selectedIntegration?.id;
      const keep = currentId ? rows.find(r => String(r.id) === String(currentId)) : null;
      const next = keep || rows[0];
      setSelectedIntegration(next);
      setEditForm({
        id: next.id,
        provider: next.provider,
        status: next.status,
        enqueue_to_message_pool: !!next?.settings?.enqueue_to_message_pool,
        default_country: next?.settings?.default_country || 'TR',
        webhook_secret: ''
      });
    } else {
      setSelectedIntegration(null);
      setEditForm(null);
      setEvents([]);
      setEventsTotal(0);
    }
  }

  async function loadEvents({ overrideOffset, overrideLimit, overrideFilter } = {}) {
    if (!selectedIntegration) return;
    setEventsLoading(true);
    setError('');

    const nextOffset = overrideOffset !== undefined ? overrideOffset : eventsOffset;
    const nextLimit = overrideLimit !== undefined ? overrideLimit : eventsLimit;
    const nextFilter = overrideFilter || eventsFilter;

    try {
      const res = await api.get(`/firms/${firmSlug}/integrations/${selectedIntegration.id}/events`, {
        headers: getFirmHeaders(firmSlug),
        params: {
          limit: nextLimit,
          offset: nextOffset,
          status: nextFilter.status || undefined,
          event_type: nextFilter.event_type || undefined,
          provider_event_id: nextFilter.provider_event_id || undefined,
          raw_body_sha256: nextFilter.raw_body_sha256 || undefined,
          payload_sha256: nextFilter.payload_sha256 || undefined
        }
      });

      // List is ordered by created_at desc in repo; keep newest first.
      setEvents(res?.data?.data || []);
      setEventsTotal(Number(res?.data?.total || 0));
      setEventsOffset(nextOffset);
      setEventsLimit(nextLimit);
      setEventsFilter(nextFilter);
    } catch (e) {
      setError(e?.error || e?.message || 'Event listesi yüklenemedi');
    } finally {
      setEventsLoading(false);
    }
  }

  useEffect(() => {
    if (!firmSlug) return;

    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        await loadIntegrations();
      } catch (e) {
        if (!mounted) return;
        setError(e?.error || e?.message || 'Entegrasyonlar yüklenemedi');
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [firmSlug]);

  useEffect(() => {
    if (!selectedIntegration) return;
    setEventsOffset(0);
    loadEvents({ overrideOffset: 0 });
  }, [selectedIntegration?.id]);

  async function onCreateIntegration() {
    setError('');
    setSuccess('');

    try {
      setLoading(true);
      const secrets = {};
      if (createForm.webhook_secret && String(createForm.webhook_secret).trim()) {
        secrets.webhook_secret = String(createForm.webhook_secret).trim();
      }

      const settings = {
        enqueue_to_message_pool: !!createForm.enqueue_to_message_pool,
        default_country: String(createForm.default_country || 'TR').trim() || 'TR'
      };

      const res = await api.post(
        `/firms/${firmSlug}/integrations`,
        {
          provider: createForm.provider,
          status: createForm.status,
          settings,
          secrets
        },
        { headers: getFirmHeaders(firmSlug) }
      );

      const created = res?.data?.data;
      setSuccess('Entegrasyon oluşturuldu');
      setCreateForm(prev => ({ ...prev, webhook_secret: '' }));
      await loadIntegrations({ selectId: created?.id });
    } catch (e) {
      setError(e?.error || e?.message || 'Entegrasyon oluşturulamadı');
    } finally {
      setLoading(false);
    }
  }

  async function onSaveIntegration() {
    if (!selectedIntegration || !editForm) return;
    setError('');
    setSuccess('');

    try {
      setEditSaving(true);

      const secrets = {};
      if (editForm.webhook_secret && String(editForm.webhook_secret).trim()) {
        secrets.webhook_secret = String(editForm.webhook_secret).trim();
      }

      const settings = {
        enqueue_to_message_pool: !!editForm.enqueue_to_message_pool,
        default_country: String(editForm.default_country || 'TR').trim() || 'TR'
      };

      await api.put(
        `/firms/${firmSlug}/integrations/${selectedIntegration.id}`,
        {
          status: editForm.status,
          settings,
          secrets: Object.keys(secrets).length ? secrets : undefined
        },
        { headers: getFirmHeaders(firmSlug) }
      );

      setSuccess('Entegrasyon güncellendi');
      setEditForm(prev => ({ ...prev, webhook_secret: '' }));
      await loadIntegrations({ selectId: selectedIntegration.id });
    } catch (e) {
      setError(e?.error || e?.message || 'Güncelleme başarısız');
    } finally {
      setEditSaving(false);
    }
  }

  async function onDisableIntegration() {
    if (!selectedIntegration) return;
    if (!window.confirm('Bu entegrasyon devre dışı bırakılsın mı?')) return;

    setError('');
    setSuccess('');

    try {
      setLoading(true);
      await api.delete(`/firms/${firmSlug}/integrations/${selectedIntegration.id}`, {
        headers: getFirmHeaders(firmSlug)
      });
      setSuccess('Entegrasyon devre dışı bırakıldı');
      await loadIntegrations({ selectId: null });
    } catch (e) {
      setError(e?.error || e?.message || 'Devre dışı bırakılamadı');
    } finally {
      setLoading(false);
    }
  }

  async function onProcess() {
    if (!selectedIntegration) return;
    setProcessErr('');
    setProcessResult(null);

    try {
      setProcessLoading(true);
      const res = await api.post(
        `/firms/${firmSlug}/integrations/${selectedIntegration.id}/process`,
        { limit: 50 },
        { headers: getFirmHeaders(firmSlug) }
      );
      setProcessResult(res?.data?.data || null);
      await loadEvents({ overrideOffset: 0 });
    } catch (e) {
      setProcessErr(e?.error || e?.message || 'Process çalıştırılamadı');
    } finally {
      setProcessLoading(false);
    }
  }

  async function onSendTestWebhook() {
    if (!selectedIntegration) return;

    setTestErr('');
    setTestResult(null);

    try {
      setTestSending(true);

      const payload = safeJsonParse(testForm.payload_json || '{}', null);
      if (!payload) throw new Error('Payload JSON geçersiz');
      const headers = safeJsonParse(testForm.headers_json || '{}', null);
      if (!headers) throw new Error('Headers JSON geçersiz');

      const res = await api.post(
        `/firms/${firmSlug}/integrations/${selectedIntegration.id}/test-webhook`,
        {
          provider: selectedIntegration.provider,
          event_type: testForm.event_type,
          provider_event_id: testForm.provider_event_id,
          payload,
          headers,
          use_provided_signature: !!testForm.use_provided_signature,
          force_bad_signature: !!testForm.force_bad_signature
        },
        { headers: getFirmHeaders(firmSlug) }
      );

      setTestResult(res?.data || null);
      await loadEvents({ overrideOffset: 0 });
    } catch (e) {
      setTestErr(e?.message || e?.error || 'Test gönderilemedi');
    } finally {
      setTestSending(false);
    }
  }

  if (!firmSlug) {
    return (
      <div style={{ padding: '12px', background: '#fff3cd', borderRadius: '10px' }}>
        Firma seçilemedi (firmSlug yok).
      </div>
    );
  }

  return (
    <div>
      {(error || success) && (
        <div style={{ marginBottom: '16px' }}>
          {error && (
            <div style={{ background: '#f8d7da', color: '#721c24', padding: '12px', borderRadius: '10px' }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{ background: '#d4edda', color: '#155724', padding: '12px', borderRadius: '10px', marginTop: error ? '10px' : 0 }}>
              {success}
            </div>
          )}
        </div>
      )}

      {/* Create */}
      <div style={{ background: '#f8f9fa', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: 0 }}>Yeni Webhook Entegrasyonu</h3>
            <div style={{ color: '#6c757d', fontSize: '13px', marginTop: '4px' }}>
              1) Provider seç → 2) Secret gir → 3) Webhook URL’yi kopyala
            </div>
          </div>
          <button
            onClick={onCreateIntegration}
            disabled={loading}
            style={{
              background: '#28a745',
              color: 'white',
              border: 'none',
              padding: '10px 14px',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            Oluştur
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px', marginTop: '12px' }}>
          <div>
            <div style={{ fontWeight: 600, marginBottom: '6px' }}>Provider</div>
            <select
              value={createForm.provider}
              onChange={e => setCreateForm(prev => ({ ...prev, provider: e.target.value }))}
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #dee2e6' }}
            >
              {PROVIDERS.map(p => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div style={{ fontWeight: 600, marginBottom: '6px' }}>Webhook Secret</div>
            <input
              type="password"
              value={createForm.webhook_secret}
              onChange={e => setCreateForm(prev => ({ ...prev, webhook_secret: e.target.value }))}
              placeholder="(panelde gösterilmez)"
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #dee2e6' }}
            />
            <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
              Secret panelde gösterilmez. Kaybedersen yeniden gir.
            </div>
          </div>
        </div>

        <div style={{ marginTop: '10px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => setUi(prev => ({ ...prev, createAdvanced: !prev.createAdvanced }))}
            style={{ background: 'transparent', border: '1px solid #dee2e6', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}
          >
            {ui.createAdvanced ? 'Gelişmiş’i gizle' : 'Gelişmiş ayarlar'}
          </button>
          <div style={{ color: '#6c757d', fontSize: '13px' }}>
            Varsayılanlar: active + TR + message_pool kapalı
          </div>
        </div>

        {ui.createAdvanced && (
          <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
            <div>
              <div style={{ fontWeight: 600, marginBottom: '6px' }}>Durum</div>
              <select
                value={createForm.status}
                onChange={e => setCreateForm(prev => ({ ...prev, status: e.target.value }))}
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #dee2e6' }}
              >
                <option value="active">active</option>
                <option value="disabled">disabled</option>
              </select>
            </div>

            <div>
              <div style={{ fontWeight: 600, marginBottom: '6px' }}>Default Country</div>
              <input
                value={createForm.default_country}
                onChange={e => setCreateForm(prev => ({ ...prev, default_country: e.target.value }))}
                placeholder="TR"
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #dee2e6' }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                id="enqueue_to_message_pool"
                type="checkbox"
                checked={!!createForm.enqueue_to_message_pool}
                onChange={e => setCreateForm(prev => ({ ...prev, enqueue_to_message_pool: e.target.checked }))}
              />
              <label htmlFor="enqueue_to_message_pool" style={{ fontWeight: 600 }}>
                Message Pool enqueue (opt-in)
              </label>
            </div>
          </div>
        )}
      </div>

      {/* List + Select */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 360px) 1fr', gap: '16px' }}>
        <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e9ecef', padding: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
            <h3 style={{ margin: 0 }}>Entegrasyonlar</h3>
            <button
              onClick={() => loadIntegrations()}
              disabled={loading}
              style={{ background: '#007bff', color: 'white', border: 'none', padding: '8px 10px', borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer' }}
            >
              Yenile
            </button>
          </div>

          {loading && (
            <div style={{ marginTop: '12px', color: '#6c757d' }}>Yükleniyor…</div>
          )}

          {!loading && integrations.length === 0 && (
            <div style={{ marginTop: '12px', color: '#6c757d' }}>
              Henüz entegrasyon yok.
            </div>
          )}

          <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {integrations.map(row => {
              const isActive = String(selectedIntegration?.id) === String(row.id);
              return (
                <button
                  key={row.id}
                  onClick={() => {
                    setSelectedIntegration(row);
                    setEditForm({
                      id: row.id,
                      provider: row.provider,
                      status: row.status,
                      enqueue_to_message_pool: !!row?.settings?.enqueue_to_message_pool,
                      default_country: row?.settings?.default_country || 'TR',
                      webhook_secret: ''
                    });
                  }}
                  style={{
                    textAlign: 'left',
                    border: isActive ? '2px solid #007bff' : '1px solid #e9ecef',
                    background: isActive ? '#f0f7ff' : 'white',
                    borderRadius: '10px',
                    padding: '10px',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{ fontWeight: 700 }}>{row.provider}</div>
                  <div style={{ color: '#6c757d', fontSize: '13px' }}>#{row.id} • {row.status}</div>
                  <div style={{ color: '#6c757d', fontSize: '12px', marginTop: '4px' }}>
                    Secret: {row?.secrets?.has_webhook_secret ? 'var' : 'yok'}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Details */}
        <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e9ecef', padding: '14px' }}>
          {!selectedIntegration && (
            <div style={{ color: '#6c757d' }}>Bir entegrasyon seçin.</div>
          )}

          {selectedIntegration && editForm && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                <div>
                  <h3 style={{ margin: 0 }}>{selectedIntegration.provider} Entegrasyonu</h3>
                  <div style={{ color: '#6c757d', fontSize: '13px' }}>ID: {selectedIntegration.id}</div>
                </div>

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    onClick={onSaveIntegration}
                    disabled={editSaving}
                    style={{ background: '#007bff', color: 'white', border: 'none', padding: '10px 12px', borderRadius: '8px', cursor: editSaving ? 'not-allowed' : 'pointer', fontWeight: 600 }}
                  >
                    Kaydet
                  </button>
                  <button
                    onClick={onDisableIntegration}
                    disabled={editSaving}
                    style={{ background: '#dc3545', color: 'white', border: 'none', padding: '10px 12px', borderRadius: '8px', cursor: editSaving ? 'not-allowed' : 'pointer', fontWeight: 600 }}
                  >
                    Devre Dışı
                  </button>
                </div>
              </div>

              <div style={{ marginTop: '12px', padding: '12px', background: '#f8f9fa', borderRadius: '10px' }}>
                <div style={{ fontWeight: 700, marginBottom: '8px' }}>Webhook URL</div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    readOnly
                    value={webhookUrl}
                    style={{ flex: 1, minWidth: '260px', padding: '10px', borderRadius: '8px', border: '1px solid #dee2e6', background: 'white' }}
                  />
                  <button
                    onClick={() => copyToClipboard(webhookUrl)}
                    style={{ background: '#6c757d', color: 'white', border: 'none', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}
                  >
                    Kopyala
                  </button>
                </div>
                <div style={{ marginTop: '10px', color: '#6c757d', fontSize: '13px' }}>
                  Secret durumu: {selectedIntegration?.secrets?.has_webhook_secret ? 'var' : 'yok'}
                </div>
              </div>

              <div style={{ marginTop: '12px', padding: '12px', borderRadius: '10px', border: '1px solid #e9ecef' }}>
                <div style={{ fontWeight: 700, marginBottom: '8px' }}>Kurulum (Hızlı)</div>
                <ol style={{ margin: 0, paddingLeft: '18px', color: '#495057' }}>
                  <li style={{ marginBottom: '6px' }}>Provider panelinde yeni Webhook tanımla</li>
                  <li style={{ marginBottom: '6px' }}>URL olarak yukarıdaki Webhook URL’yi yapıştır</li>
                  <li style={{ marginBottom: '6px' }}>Secret alanına panelde girdiğin secret’ı yaz</li>
                  <li style={{ marginBottom: '6px' }}>Test gönder (aşağıdaki Araçlar’dan)</li>
                </ol>

                {String(selectedIntegration.provider) === 'shopify' && (
                  <div style={{ marginTop: '10px', background: '#f8f9fa', borderRadius: '10px', padding: '10px', fontSize: '13px', color: '#495057' }}>
                    Shopify için: topic örnekleri `orders/create`, `orders/fulfilled`, `refunds/create`.
                  </div>
                )}
              </div>

              <div style={{ marginTop: '12px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setUi(prev => ({ ...prev, editAdvanced: !prev.editAdvanced }))}
                  style={{ background: 'transparent', border: '1px solid #dee2e6', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}
                >
                  {ui.editAdvanced ? 'Ayarları gizle' : 'Ayarlar'}
                </button>
                <div style={{ color: '#6c757d', fontSize: '13px' }}>
                  (Durum, ülke, secret güncelleme, message_pool)
                </div>
              </div>

              {ui.editAdvanced && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginTop: '12px' }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '6px' }}>Durum</div>
                    <select
                      value={editForm.status}
                      onChange={e => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                      style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #dee2e6' }}
                    >
                      <option value="active">active</option>
                      <option value="disabled">disabled</option>
                    </select>
                  </div>

                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '6px' }}>Default Country</div>
                    <input
                      value={editForm.default_country}
                      onChange={e => setEditForm(prev => ({ ...prev, default_country: e.target.value }))}
                      placeholder="TR"
                      style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #dee2e6' }}
                    />
                  </div>

                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '6px' }}>Webhook Secret (güncelle)</div>
                    <input
                      type="password"
                      value={editForm.webhook_secret}
                      onChange={e => setEditForm(prev => ({ ...prev, webhook_secret: e.target.value }))}
                      placeholder="(boş bırakılırsa değişmez)"
                      style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #dee2e6' }}
                    />
                    <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                      Mevcut secret panelde gösterilmez.
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '22px' }}>
                    <input
                      id="edit_enqueue_to_message_pool"
                      type="checkbox"
                      checked={!!editForm.enqueue_to_message_pool}
                      onChange={e => setEditForm(prev => ({ ...prev, enqueue_to_message_pool: e.target.checked }))}
                    />
                    <label htmlFor="edit_enqueue_to_message_pool" style={{ fontWeight: 600 }}>
                      Message Pool enqueue (opt-in)
                    </label>
                  </div>
                </div>
              )}

              {/* Tools (collapsed by default) */}
              <div style={{ marginTop: '16px', border: '1px solid #e9ecef', borderRadius: '12px', padding: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <h4 style={{ margin: 0 }}>Araçlar</h4>
                  <button
                    onClick={() => setUi(prev => ({ ...prev, toolsOpen: !prev.toolsOpen }))}
                    style={{ background: 'transparent', border: '1px solid #dee2e6', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', fontWeight: 700 }}
                  >
                    {ui.toolsOpen ? 'Gizle' : 'Aç'}
                  </button>
                </div>

                {!ui.toolsOpen && (
                  <div style={{ marginTop: '8px', color: '#6c757d', fontSize: '13px' }}>
                    Test webhook ve manuel process burada.
                  </div>
                )}

                {ui.toolsOpen && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '12px' }}>
                    <div style={{ border: '1px solid #e9ecef', borderRadius: '12px', padding: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                        <h4 style={{ margin: 0 }}>Process</h4>
                        <button
                          onClick={onProcess}
                          disabled={processLoading}
                          style={{ background: '#17a2b8', color: 'white', border: 'none', padding: '10px 12px', borderRadius: '8px', cursor: processLoading ? 'not-allowed' : 'pointer', fontWeight: 700 }}
                        >
                          Çalıştır
                        </button>
                      </div>

                      {processErr && <div style={{ marginTop: '10px', color: '#dc3545' }}>{processErr}</div>}
                      {processResult && (
                        <pre style={{ marginTop: '10px', background: '#f8f9fa', padding: '10px', borderRadius: '10px', overflow: 'auto', fontSize: '12px' }}>
                          {JSON.stringify(processResult, null, 2)}
                        </pre>
                      )}
                    </div>

                    <div style={{ border: '1px solid #e9ecef', borderRadius: '12px', padding: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                        <h4 style={{ margin: 0 }}>Test Webhook</h4>
                        <button
                          onClick={onSendTestWebhook}
                          disabled={testSending}
                          style={{ background: '#6f42c1', color: 'white', border: 'none', padding: '10px 12px', borderRadius: '8px', cursor: testSending ? 'not-allowed' : 'pointer', fontWeight: 700 }}
                        >
                          Gönder
                        </button>
                      </div>

                      <div style={{ marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => {
                            if (String(selectedIntegration.provider) === 'shopify') {
                              setTestForm(prev => ({
                                ...prev,
                                event_type: 'orders/create',
                                payload_json: '{\n  "id": 123,\n  "customer": {"first_name": "Test", "phone": "+905xxxxxxxxx"}\n}',
                                headers_json: '{\n  "x-shopify-topic": "orders/create"\n}'
                              }));
                            }
                          }}
                          style={{ background: 'transparent', border: '1px solid #dee2e6', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', fontWeight: 700 }}
                        >
                          Shopify örnek doldur
                        </button>
                      </div>

                      <div style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
                        <input
                          value={testForm.event_type}
                          onChange={e => setTestForm(prev => ({ ...prev, event_type: e.target.value }))}
                          placeholder="event_type (opsiyonel)"
                          style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #dee2e6' }}
                        />
                        <input
                          value={testForm.provider_event_id}
                          onChange={e => setTestForm(prev => ({ ...prev, provider_event_id: e.target.value }))}
                          placeholder="provider_event_id (opsiyonel)"
                          style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #dee2e6' }}
                        />
                        <textarea
                          value={testForm.payload_json}
                          onChange={e => setTestForm(prev => ({ ...prev, payload_json: e.target.value }))}
                          rows={6}
                          style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #dee2e6', fontFamily: 'monospace', fontSize: '12px' }}
                        />
                        <textarea
                          value={testForm.headers_json}
                          onChange={e => setTestForm(prev => ({ ...prev, headers_json: e.target.value }))}
                          rows={4}
                          style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #dee2e6', fontFamily: 'monospace', fontSize: '12px' }}
                        />

                        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="checkbox"
                              checked={!!testForm.use_provided_signature}
                              onChange={e => setTestForm(prev => ({ ...prev, use_provided_signature: e.target.checked }))}
                            />
                            use_provided_signature
                          </label>

                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="checkbox"
                              checked={!!testForm.force_bad_signature}
                              onChange={e => setTestForm(prev => ({ ...prev, force_bad_signature: e.target.checked }))}
                            />
                            force_bad_signature
                          </label>
                        </div>

                        {testErr && <div style={{ color: '#dc3545' }}>{testErr}</div>}
                        {testResult && (
                          <pre style={{ background: '#f8f9fa', padding: '10px', borderRadius: '10px', overflow: 'auto', fontSize: '12px' }}>
                            {JSON.stringify(testResult, null, 2)}
                          </pre>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Events */}
              <div style={{ marginTop: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <h4 style={{ margin: 0 }}>Events</h4>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <select
                      value={eventsLimit}
                      onChange={e => loadEvents({ overrideLimit: Number(e.target.value), overrideOffset: 0 })}
                      style={{ padding: '10px', borderRadius: '8px', border: '1px solid #dee2e6' }}
                    >
                      {[10, 25, 50, 100].map(n => (
                        <option key={n} value={n}>
                          {n}/sayfa
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => loadEvents({ overrideOffset: 0 })}
                      disabled={eventsLoading}
                      style={{ background: '#007bff', color: 'white', border: 'none', padding: '10px 12px', borderRadius: '8px', cursor: eventsLoading ? 'not-allowed' : 'pointer', fontWeight: 700 }}
                    >
                      Yenile
                    </button>
                  </div>
                </div>

                <div style={{ marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ color: '#6c757d', fontSize: '13px' }}>
                    Son event: {eventsStats.latest?.created_at || '-'}
                  </div>
                  <div style={{ color: '#6c757d', fontSize: '13px' }}>
                    failed: {eventsStats.counts.failed} • retry: {eventsStats.counts.retry} • received: {eventsStats.counts.received} • processed: {eventsStats.counts.processed}
                  </div>
                </div>

                <div style={{ marginTop: '10px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => {
                      const next = { ...eventsFilter, status: 'failed' };
                      loadEvents({ overrideFilter: next, overrideOffset: 0 });
                    }}
                    style={{ background: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', padding: '8px 10px', borderRadius: '999px', cursor: 'pointer', fontWeight: 700 }}
                  >
                    failed
                  </button>
                  <button
                    onClick={() => {
                      const next = { ...eventsFilter, status: 'retry' };
                      loadEvents({ overrideFilter: next, overrideOffset: 0 });
                    }}
                    style={{ background: '#fff3cd', color: '#856404', border: '1px solid #ffeeba', padding: '8px 10px', borderRadius: '999px', cursor: 'pointer', fontWeight: 700 }}
                  >
                    retry
                  </button>
                  <button
                    onClick={() => {
                      const next = { ...eventsFilter, status: '' };
                      loadEvents({ overrideFilter: next, overrideOffset: 0 });
                    }}
                    style={{ background: '#e2e3e5', color: '#383d41', border: '1px solid #d6d8db', padding: '8px 10px', borderRadius: '999px', cursor: 'pointer', fontWeight: 700 }}
                  >
                    tümü
                  </button>

                  <button
                    onClick={() => setUi(prev => ({ ...prev, filtersOpen: !prev.filtersOpen }))}
                    style={{ background: 'transparent', border: '1px solid #dee2e6', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', fontWeight: 700 }}
                  >
                    {ui.filtersOpen ? 'Filtreleri gizle' : 'Gelişmiş filtreler'}
                  </button>
                </div>

                {ui.filtersOpen && (
                  <div style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                    <select
                      value={eventsFilter.status}
                      onChange={e => {
                        const next = { ...eventsFilter, status: e.target.value };
                        loadEvents({ overrideFilter: next, overrideOffset: 0 });
                      }}
                      style={{ padding: '10px', borderRadius: '8px', border: '1px solid #dee2e6' }}
                    >
                      <option value="">status (tümü)</option>
                      <option value="received">received</option>
                      <option value="processing">processing</option>
                      <option value="retry">retry</option>
                      <option value="processed">processed</option>
                      <option value="ignored">ignored</option>
                      <option value="failed">failed</option>
                    </select>
                    <input
                      value={eventsFilter.event_type}
                      onChange={e => setEventsFilter(prev => ({ ...prev, event_type: e.target.value }))}
                      placeholder="event_type"
                      style={{ padding: '10px', borderRadius: '8px', border: '1px solid #dee2e6' }}
                    />
                    <input
                      value={eventsFilter.provider_event_id}
                      onChange={e => setEventsFilter(prev => ({ ...prev, provider_event_id: e.target.value }))}
                      placeholder="provider_event_id"
                      style={{ padding: '10px', borderRadius: '8px', border: '1px solid #dee2e6' }}
                    />
                    <input
                      value={eventsFilter.raw_body_sha256}
                      onChange={e => setEventsFilter(prev => ({ ...prev, raw_body_sha256: e.target.value }))}
                      placeholder="raw_body_sha256"
                      style={{ padding: '10px', borderRadius: '8px', border: '1px solid #dee2e6' }}
                    />
                    <input
                      value={eventsFilter.payload_sha256}
                      onChange={e => setEventsFilter(prev => ({ ...prev, payload_sha256: e.target.value }))}
                      placeholder="payload_sha256"
                      style={{ padding: '10px', borderRadius: '8px', border: '1px solid #dee2e6' }}
                    />

                    <button
                      onClick={() => loadEvents({ overrideFilter: eventsFilter, overrideOffset: 0 })}
                      disabled={eventsLoading}
                      style={{ background: '#343a40', color: 'white', border: 'none', padding: '10px 12px', borderRadius: '8px', cursor: eventsLoading ? 'not-allowed' : 'pointer', fontWeight: 700 }}
                    >
                      Filtrele
                    </button>
                  </div>
                )}

                <div style={{ marginTop: '10px', color: '#6c757d', fontSize: '13px' }}>
                  Toplam: {eventsTotal} • Sayfa: {Math.floor(eventsOffset / eventsLimit) + 1}
                </div>

                <div style={{ marginTop: '10px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button
                    onClick={() => loadEvents({ overrideOffset: Math.max(0, eventsOffset - eventsLimit) })}
                    disabled={eventsLoading || eventsOffset <= 0}
                    style={{ background: '#6c757d', color: 'white', border: 'none', padding: '8px 10px', borderRadius: '8px', cursor: eventsLoading || eventsOffset <= 0 ? 'not-allowed' : 'pointer' }}
                  >
                    ← Önceki
                  </button>
                  <button
                    onClick={() => loadEvents({ overrideOffset: eventsOffset + eventsLimit })}
                    disabled={eventsLoading || eventsOffset + eventsLimit >= eventsTotal}
                    style={{ background: '#6c757d', color: 'white', border: 'none', padding: '8px 10px', borderRadius: '8px', cursor: eventsLoading || eventsOffset + eventsLimit >= eventsTotal ? 'not-allowed' : 'pointer' }}
                  >
                    Sonraki →
                  </button>

                  {eventsLoading && <span style={{ color: '#6c757d' }}>Yükleniyor…</span>}
                </div>

                <div style={{ marginTop: '12px', border: '1px solid #e9ecef', borderRadius: '12px', overflow: 'hidden' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '120px 110px 1fr 220px', background: '#f8f9fa', padding: '10px', fontWeight: 700, fontSize: '13px' }}>
                    <div>Status</div>
                    <div>Type</div>
                    <div>Provider Event</div>
                    <div>Created</div>
                  </div>

                  {events.length === 0 && !eventsLoading && (
                    <div style={{ padding: '12px', color: '#6c757d' }}>Event yok.</div>
                  )}

                  {events.map(ev => (
                    <div
                      key={ev.id}
                      onClick={() => openEventModal(ev)}
                      style={{ display: 'grid', gridTemplateColumns: '120px 110px 1fr 220px', padding: '10px', borderTop: '1px solid #e9ecef', fontSize: '13px', cursor: 'pointer' }}
                    >
                      <div>{ev.status}</div>
                      <div>{ev.event_type}</div>
                      <div style={{ wordBreak: 'break-all' }}>{ev.provider_event_id}</div>
                      <div>{ev.created_at}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {eventModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={() => {
            setEventModalOpen(false);
            setEventModalErr('');
            setEventModalData(null);
            setEventModalActions([]);
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '16px',
              width: '900px',
              maxWidth: '95vw',
              maxHeight: '85vh',
              overflow: 'auto'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
              <h3 style={{ margin: 0 }}>Event Detayı</h3>
              <button
                onClick={() => {
                  setEventModalOpen(false);
                  setEventModalErr('');
                  setEventModalData(null);
                  setEventModalActions([]);
                }}
                style={{ background: '#6c757d', color: 'white', border: 'none', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', fontWeight: 700 }}
              >
                Kapat
              </button>
            </div>

            {eventModalLoading && <div style={{ marginTop: '12px', color: '#6c757d' }}>Yükleniyor…</div>}
            {eventModalErr && <div style={{ marginTop: '12px', color: '#dc3545' }}>{eventModalErr}</div>}

            {eventModalData && (
              <div style={{ marginTop: '12px' }}>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => copyToClipboard(eventModalData?.raw_body_sha256 || '')}
                    style={{ background: 'transparent', border: '1px solid #dee2e6', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', fontWeight: 700 }}
                  >
                    raw_body_sha256 kopyala
                  </button>
                  <button
                    onClick={() => copyToClipboard(eventModalData?.payload_sha256 || '')}
                    style={{ background: 'transparent', border: '1px solid #dee2e6', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', fontWeight: 700 }}
                  >
                    payload_sha256 kopyala
                  </button>
                  <button
                    onClick={() => copyToClipboard(JSON.stringify(eventModalData?.payload_json || {}, null, 2))}
                    style={{ background: 'transparent', border: '1px solid #dee2e6', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', fontWeight: 700 }}
                  >
                    payload kopyala
                  </button>
                </div>

                <pre style={{ marginTop: '12px', background: '#f8f9fa', padding: '12px', borderRadius: '12px', overflow: 'auto', fontSize: '12px' }}>
                  {JSON.stringify(eventModalData, null, 2)}
                </pre>

                <div style={{ marginTop: '12px', fontWeight: 700 }}>Aksiyonlar</div>
                {eventModalActions.length === 0 ? (
                  <div style={{ color: '#6c757d', fontSize: '13px', marginTop: '6px' }}>Aksiyon kaydı yok.</div>
                ) : (
                  <pre style={{ marginTop: '8px', background: '#f8f9fa', padding: '12px', borderRadius: '12px', overflow: 'auto', fontSize: '12px' }}>
                    {JSON.stringify(eventModalActions, null, 2)}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
